import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

interface Props {
  unaId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLogged?: () => void;
}

/**
 * Manual resolution log dialog. For members backfilling older paper resolutions
 * or logging a resolution that was uploaded to the Record outside a portal
 * workflow. Number defaults to the next available; the member may override to
 * fill a gap. Duplicates are blocked server-side by a unique constraint.
 */
export function ManualResolutionLogDialog({ unaId, open, onOpenChange, onLogged }: Props) {
  const [loadingNumber, setLoadingNumber] = useState(false);
  const [nextNumber, setNextNumber] = useState<number | null>(null);
  const [resolutionNumber, setResolutionNumber] = useState<string>("");
  const [title, setTitle] = useState("");
  const [adoptedDate, setAdoptedDate] = useState<string>(
    new Date().toISOString().split("T")[0],
  );
  const [recordDocumentId, setRecordDocumentId] = useState<string>("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [recordDocs, setRecordDocs] = useState<Array<{ id: string; document_name: string }>>([]);

  useEffect(() => {
    if (!open || !unaId) return;
    (async () => {
      setLoadingNumber(true);
      const { data, error } = await (supabase.rpc as any)("next_resolution_number", {
        _una_id: unaId,
      });
      if (!error && typeof data === "number") {
        setNextNumber(data);
        setResolutionNumber(String(data));
      }
      setLoadingNumber(false);

      // Preload Record documents (finalized generated_documents for the una) so
      // members can link a stored copy of the resolution to the registry row.
      const { data: docs } = await supabase
        .from("generated_documents")
        .select("id, document_name")
        .eq("una_id", unaId)
        .order("generated_at", { ascending: false })
        .limit(50);
      setRecordDocs((docs || []) as any);
    })();
  }, [open, unaId]);

  const reset = () => {
    setTitle("");
    setNotes("");
    setRecordDocumentId("");
    setAdoptedDate(new Date().toISOString().split("T")[0]);
    setResolutionNumber("");
    setNextNumber(null);
  };

  const handleSubmit = async () => {
    const num = parseInt(resolutionNumber, 10);
    if (!num || num < 1) {
      toast.error("Enter a resolution number of 1 or higher.");
      return;
    }
    if (!title.trim()) {
      toast.error("Enter a title.");
      return;
    }
    if (!adoptedDate) {
      toast.error("Choose an adopted date.");
      return;
    }
    setSubmitting(true);
    try {
      // allocate_resolution_number validates the requested number under an
      // advisory lock, so two members can't grab the same number at once.
      const { data: allocated, error: allocErr } = await (supabase.rpc as any)("allocate_resolution_number",
        { _una_id: unaId, _requested: num },
      );
      if (allocErr) {
        toast.error(
          allocErr.message?.includes("already assigned")
            ? `Number ${num} is already taken. Try another.`
            : "Could not reserve that number. Try again.",
        );
        setSubmitting(false);
        return;
      }
      const { error: insertErr } = await (supabase.from as any)("resolution_registry").insert({
        una_id: unaId,
        resolution_number: allocated as any,
        source: "manual_upload",
        record_document_id: recordDocumentId || null,
        title: title.trim(),
        adopted_date: adoptedDate,
        notes: notes.trim() || null,
      });
      if (insertErr) {
        toast.error(
          insertErr.message?.includes("duplicate")
            ? `Number ${num} is already taken.`
            : "Could not log that resolution. Try again.",
        );
        setSubmitting(false);
        return;
      }
      toast.success(`Resolution #${allocated} logged.`);
      reset();
      onOpenChange(false);
      onLogged?.();
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Log a manual resolution</DialogTitle>
          <DialogDescription>
            Record a resolution that lives outside a portal workflow (older paper
            resolutions or a document you already uploaded to the Record).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="res-number">Resolution number</Label>
            <Input
              id="res-number"
              type="number"
              min={1}
              value={resolutionNumber}
              onChange={(e) => setResolutionNumber(e.target.value)}
              disabled={loadingNumber}
            />
            <p className="text-xs text-muted-foreground">
              {loadingNumber
                ? "Checking next available…"
                : nextNumber !== null
                  ? `Next available number: ${nextNumber}. Change it to backfill an older resolution.`
                  : "Numbers are unique per association."}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="res-title">Title</Label>
            <Input
              id="res-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Adopting the annual budget"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="res-date">Adopted date</Label>
            <Input
              id="res-date"
              type="date"
              value={adoptedDate}
              onChange={(e) => setAdoptedDate(e.target.value)}
            />
          </div>

          {recordDocs.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="res-doc">Linked document on the Record (optional)</Label>
              <select
                id="res-doc"
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                value={recordDocumentId}
                onChange={(e) => setRecordDocumentId(e.target.value)}
              >
                <option value="">None</option>
                {recordDocs.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.document_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="res-notes">Notes (optional)</Label>
            <Textarea
              id="res-notes"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything worth remembering about this entry."
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Logging…
              </>
            ) : (
              "Log resolution"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
